var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useState, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import moment from 'moment-timezone';
import TextField from '../TextField';
import Dropdown from '../Dropdown';
import { DAYS, SERVICE_STATUS } from '../../const';
import { convertDateTimeToDayIndex, convertDayIndexToDateTime, generateDayTimeOptions, } from '../../utils';
import { config } from './Config';
import Button from '../base/Button/Button';
import { Grid } from '../base/Grid/Grid';
export function Form(_a) {
    var fromApp = _a.fromApp, rotation = _a.rotation, rotations = _a.rotations, contacts = _a.contacts, onChange = _a.onChange, onClose = _a.onClose, _b = _a.viewMode, viewMode = _b === void 0 ? false : _b, _c = _a.isOverrideMode, isOverrideMode = _c === void 0 ? false : _c, slotDate = _a.slotDate, timezone = _a.timezone;
    var _d = useState(null), overlapError = _d[0], setOverlapError = _d[1];
    var _e = useForm({
        defaultValues: rotation,
        mode: 'onChange',
        reValidateMode: 'onChange',
    }), handleSubmit = _e.handleSubmit, control = _e.control, watch = _e.watch, setValue = _e.setValue, _f = _e.formState, isValid = _f.isValid, errors = _f.errors;
    var from = watch('from');
    var to = watch('to');
    var serviceStatus = watch('serviceStatus');
    /* Old duplicate validation, can be removed if we don't need to validate technician
      const primaryTechnician = watch('primaryTechnician');
      const backupTechnician = watch('backupTechnician');
      const finalTechnician = watch('finalTechnician');
    */
    var dateValue = watch('date');
    // Check if this is a new override (not yet saved in rotations list)
    var isNewOverride = serviceStatus.startsWith('override')
        && (!(rotation === null || rotation === void 0 ? void 0 : rotation.id) || !rotations.some(function (r) { return r.id === rotation.id; }));
    // When isOverrideMode or creating a new override, use "new" behavior (no delete, save label)
    var baseConfig = config[serviceStatus];
    var formConfig = isOverrideMode || isNewOverride
        ? __assign(__assign({}, baseConfig), { delete: false, btnLabel: 'Save Rotation' }) : baseConfig;
    var serviceStatusOptions = SERVICE_STATUS.filter(function (status) { return formConfig.status.includes(status.value || ''); });
    useEffect(function () {
        if (overlapError) {
            setOverlapError(null);
        }
    }, [from, to, dateValue]);
    useEffect(function () {
        if (serviceStatus === 'override' && !dateValue && slotDate) {
            setValue('date', slotDate);
        }
    }, [serviceStatus, slotDate, dateValue, setValue]);
    // Reset from/to when date changes to refresh available time options
    var prevDateRef = useRef(dateValue);
    useEffect(function () {
        if (dateValue && serviceStatus.startsWith('override') && prevDateRef.current !== dateValue) {
            // Reset from and to to force user to re-select with new available options
            setValue('from', -1);
            setValue('to', -1);
        }
        prevDateRef.current = dateValue;
    }, [dateValue, serviceStatus, setValue]);
    var checkOverlapWithExistingOverrides = function (data) {
        if (!data.serviceStatus.startsWith('override'))
            return null;
        var existingOverrides = rotations.filter(function (r) { return r.serviceStatus.startsWith('override') && r.id !== (rotation === null || rotation === void 0 ? void 0 : rotation.id); });
        var hasOverlap = existingOverrides.some(function (existing) {
            var sameDate = data.date && existing.date && data.date === existing.date;
            if (sameDate) {
                var newFromHour = data.from % 24;
                var newToHour = data.to % 24;
                var existingFromHour = existing.from % 24;
                var existingToHour = existing.to % 24;
                return newFromHour < existingToHour && newToHour > existingFromHour;
            }
            return false;
        });
        return hasOverlap ? 'This override overlaps with an existing override on the same date.' : null;
    };
    var onSubmit = function (data) {
        var overlap = checkOverlapWithExistingOverrides(data);
        if (overlap) {
            setOverlapError(overlap);
            return;
        }
        try {
            if (data) {
                var updatedData = __assign({}, data);
                onChange({ rotation: updatedData, action: 'UPDATE' });
            }
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to save rotation', error);
        }
        finally {
            onClose();
        }
    };
    var onDelete = function () {
        if (rotation) {
            onChange({ rotation: rotation, action: 'DELETE' });
            onClose();
        }
    };
    /* Old duplicate validation, can be removed if we don't need to validate technician
      const getTechnicians = (techniciansToFilter: string[], selectedTechnician?: string) => contacts?.filter(
        (tech) => tech.id === selectedTechnician || !techniciansToFilter.includes(tech.id || ''),
      )?.map((tech) => ({
        value: tech.id,
        label: tech.name,
      }));
    */
    var getTechnicians = function () { return contacts === null || contacts === void 0 ? void 0 : contacts.map(function (tech) { return ({
        value: tech.id,
        label: tech.name,
    }); }); };
    var getPrevRotation = function (selectedDayIndex) {
        var currentDay = convertDayIndexToDateTime(selectedDayIndex).dayOfWeek;
        // Only rotations on the same day that end before or at the selected time
        var sortedRotations = rotations
            .filter(function (rot) {
            var rotationDay = convertDayIndexToDateTime(rot.from).dayOfWeek;
            return rotationDay === currentDay
                && rot.to <= selectedDayIndex
                && rot.id !== (rotation === null || rotation === void 0 ? void 0 : rotation.id); // Exclude current rotation;
        })
            .sort(function (a, b) { return b.to - a.to; });
        // Return the first rotation (which will be the previous one chronologically on the same day)
        return sortedRotations.length > 0 ? sortedRotations[0] : null;
    };
    var getNextRotation = function (selectedDayIndex) {
        var currentDay = convertDayIndexToDateTime(selectedDayIndex).dayOfWeek;
        // Only rotations on the same day that start after or at the selected time
        var sortedRotations = rotations
            .filter(function (rot) {
            var rotationDay = convertDayIndexToDateTime(rot.from).dayOfWeek;
            return rotationDay === currentDay
                && !rot.serviceStatus.startsWith('override') // exclude overrides
                && rot.from >= selectedDayIndex // Shoud be greater than the selected time
                && rot.id !== (rotation === null || rotation === void 0 ? void 0 : rotation.id); // Exclude current rotation
        })
            .sort(function (a, b) { return a.from - b.from; });
        // Return the first rotation (which will be the next one chronologically on the same day)
        return sortedRotations.length > 0 ? sortedRotations[0] : null;
    };
    var getConfig = function (field) {
        var effectiveFrom = Math.max(0, from);
        var min = (field === 'from') ? 0 : convertDayIndexToDateTime(effectiveFrom).hours + 1;
        var max = 24;
        var sub = (field === 'from') ? 1 : 0;
        // When from is not yet set (-1), derive the day of week from the selected date
        var dwFrom = convertDayIndexToDateTime(effectiveFrom).dayOfWeek;
        if (from < 0 && dateValue) {
            dwFrom = moment(dateValue, 'YYYY-MM-DD').isoWeekday() - 1; // isoWeekday: 1=Mon...7=Sun → 0=Mon...6=Sun
        }
        // For 'to' field, don't use current 'to' value to avoid circular dependency
        var dwTo = (field === 'from' ? convertDayIndexToDateTime(Math.max(0, to)) : { dayOfWeek: dwFrom }).dayOfWeek;
        var nextRotation = getNextRotation(from);
        var prevRotation = getPrevRotation(from);
        var isOverride = serviceStatus.startsWith('override');
        var day = {
            value: dwFrom.toString(),
            label: DAYS[dwFrom],
        };
        var minHour = min;
        var maxHour = max;
        var minMinute = 0;
        var maxMinute = 45; // Último minuto disponible (45)
        // For overrides, filter out past hours when date is today
        if (isOverride && dateValue && field === 'from') {
            var hasValidTimezone = Boolean(timezone && moment.tz.zone(timezone));
            var now = hasValidTimezone ? moment().tz(timezone) : moment();
            var selectedDate = hasValidTimezone
                ? moment.tz(dateValue, 'YYYY-MM-DD', timezone)
                : moment(dateValue, 'YYYY-MM-DD');
            if (selectedDate.isSame(now, 'day')) {
                var currentHour = now.hour();
                var currentMinute = now.minute();
                // Round up to next 15-minute interval
                var roundedMinute = Math.ceil(currentMinute / 15) * 15;
                if (roundedMinute >= 60) {
                    minHour = Math.max(minHour, currentHour + 1);
                    minMinute = 0;
                }
                else {
                    minHour = Math.max(minHour, currentHour);
                    minMinute = Math.max(minMinute, roundedMinute);
                }
            }
        }
        // Set minimum time based on previous rotation
        if (prevRotation && !isOverride) {
            var _a = convertDayIndexToDateTime(prevRotation.to), hours = _a.hours, minutes = _a.minutes;
            minHour = Math.max(0, hours);
            minMinute = minutes;
        }
        // Set maximum time based on next rotation
        if (nextRotation && !isOverride) {
            var _b = convertDayIndexToDateTime(nextRotation.from), hours = _b.hours, minutes = _b.minutes;
            maxHour = Math.min(23, hours - sub);
            maxMinute = minutes;
            // If is from, max hour and minute can be the same as next rotation hour
            if (field === 'from') {
                maxHour = Math.min(23, hours);
                maxMinute = minutes;
            }
            // Si el minuto máximo es negativo, ajustar la hora
            if (maxMinute < 0) {
                maxHour = Math.max(0, maxHour - 1);
                maxMinute = 45; // Último minuto de la hora anterior
            }
        }
        if (dwFrom < dwTo && field === 'to') {
            // If the day of week from is less than to, we can use the full range
            minHour = 0;
            maxHour = 23;
            minMinute = 0;
            maxMinute = 45;
        }
        var options = generateDayTimeOptions({
            day: day,
            minHour: minHour,
            maxHour: maxHour,
            minMinute: minMinute,
            maxMinute: maxMinute,
        });
        return options;
    };
    return (React.createElement("div", { "data-testid": "tech-rotation-form" },
        React.createElement("form", { onSubmit: handleSubmit(onSubmit), "data-testid": "tech-rotation-form-element" },
            React.createElement(Grid, { container: true, spacing: 1 },
                React.createElement(Grid, { item: true, xs: formConfig.date ? 8 : 12 },
                    React.createElement(Controller, { name: "serviceStatus", control: control, render: function (_a) {
                            var field = _a.field;
                            return (React.createElement(Dropdown, __assign({}, field, { label: "Service Status", origin: fromApp, options: serviceStatusOptions || [], disabled: viewMode || isOverrideMode, "data-testid": "tech-rotation-service-status" })));
                        } })),
                formConfig.date && (React.createElement(React.Fragment, null,
                    React.createElement(Grid, { item: true, xs: 4, style: fromApp === 'spp' ? { alignSelf: 'flex-end' } : undefined },
                        React.createElement(Controller, { name: "date", control: control, rules: {
                                validate: function (value) {
                                    // Validate date is not in the past
                                    if (value) {
                                        var today = new Date().toISOString().split('T')[0];
                                        if (value < today) {
                                            return 'Date cannot be in the past.';
                                        }
                                    }
                                    return true;
                                },
                            }, render: function (_a) {
                                var _b;
                                var field = _a.field;
                                return (React.createElement(TextField, { label: "Date", name: field.name, value: field.value, onChange: function (e) {
                                        field.onChange(e.target.value);
                                    }, origin: fromApp, error: !!errors.date, helperText: (_b = errors.date) === null || _b === void 0 ? void 0 : _b.message, disabled: viewMode || formConfig.date === 'disabled', min: new Date().toISOString().split('T')[0] }));
                            } })))),
                React.createElement(Grid, { item: true, xs: 6 },
                    React.createElement(Controller, { name: "from", control: control, rules: { validate: function (v) { return v >= 0 || 'Required'; } }, render: function (_a) {
                            var field = _a.field;
                            return (React.createElement(Dropdown, __assign({}, field, { value: field.value.toString() || '0', label: "Start Time", origin: fromApp, onChange: function (dayIndex) {
                                    var fromHours = convertDayIndexToDateTime(parseInt(dayIndex, 0)).hours;
                                    if (to < 0) {
                                        // End time not yet selected, reset it so user picks it
                                        setValue('to', -1);
                                    }
                                    else {
                                        var _a = convertDayIndexToDateTime(to), dayOfWeek = _a.dayOfWeek, toHours = _a.hours, minutes = _a.minutes;
                                        if (toHours < fromHours) {
                                            // If the end time is before the start time, set the end time to the next hour
                                            var newTo = convertDateTimeToDayIndex(dayOfWeek, fromHours + 1, minutes);
                                            setValue('to', newTo);
                                        }
                                    }
                                    field.onChange(dayIndex);
                                }, options: getConfig('from'), disabled: viewMode, "data-testid": "tech-rotation-start-time" })));
                        } })),
                React.createElement(Grid, { item: true, xs: 6 },
                    React.createElement(Controller, { name: "to", control: control, rules: { validate: function (v) { return v >= 0 || 'Required'; } }, render: function (_a) {
                            var field = _a.field;
                            return (React.createElement(Dropdown, __assign({}, field, { label: "End Time", value: field.value.toString() || '0', origin: fromApp, options: getConfig('to'), disabled: viewMode, "data-testid": "tech-rotation-end-time" })));
                        } })),
                formConfig.technician && (React.createElement(React.Fragment, null,
                    React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(Controller, { name: "primaryTechnician", control: control, rules: { required: 'Primary technician required.' }, render: function (_a) {
                                var _b;
                                var field = _a.field;
                                return (React.createElement(Dropdown, __assign({}, field, { label: "Primary Contact *", origin: fromApp, options: getTechnicians(), error: !!errors.primaryTechnician, helperText: (_b = errors.primaryTechnician) === null || _b === void 0 ? void 0 : _b.message, disabled: viewMode, "data-testid": "tech-rotation-primary-contact" })));
                            } })),
                    React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(Controller, { name: "backupTechnician", control: control, render: function (_a) {
                                var field = _a.field;
                                return (React.createElement(Dropdown, __assign({}, field, { label: "Backup Contact", origin: fromApp, options: __spreadArray([
                                        { value: null, label: 'Select' }
                                    ], (getTechnicians() || []), true), disabled: viewMode, "data-testid": "tech-rotation-backup-contact" })));
                            } })),
                    React.createElement(Grid, { item: true, xs: 12 },
                        React.createElement(Controller, { name: "finalTechnician", control: control, rules: { required: 'Final technician required.' }, render: function (_a) {
                                var _b;
                                var field = _a.field;
                                return (React.createElement(Dropdown, __assign({}, field, { label: "Final Contact *", origin: fromApp, options: getTechnicians(), error: !!errors.finalTechnician, helperText: (_b = errors.finalTechnician) === null || _b === void 0 ? void 0 : _b.message, disabled: viewMode, "data-testid": "tech-rotation-final-contact" })));
                            } })))),
                overlapError && (React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement("div", { style: {
                            color: '#d32f2f',
                            backgroundColor: '#ffebee',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            fontSize: '11px',
                        } }, overlapError))),
                React.createElement(Grid, { item: true, xs: 12, style: { marginTop: 10 } },
                    React.createElement("div", { style: { display: 'flex', justifyContent: 'flex-end', gap: '10px' }, "data-testid": "tech-rotation-form-actions" },
                        React.createElement(Button, { onClick: onClose, color: "info", type: "button", size: "small", origin: fromApp, "data-testid": "tech-rotation-cancel-btn" }, "Cancel"),
                        formConfig.save && rotation && !viewMode && (React.createElement(Button, { type: "submit", color: "primary", size: "small", origin: fromApp, disabled: !isValid, "data-testid": "tech-rotation-save-btn" }, formConfig.btnLabel)),
                        formConfig.delete && rotation && !viewMode && (React.createElement(Button, { onClick: onDelete, color: "error", type: "button", size: "small", origin: fromApp, "data-testid": "tech-rotation-delete-btn" }, "Delete"))))))));
}
//# sourceMappingURL=Form.js.map